import axios from 'axios';
import { parseCookies, setCookie } from 'nookies';

declare module 'axios' {
  export interface AxiosRequestConfig {
    requiresAuth?: boolean;
    cacheTtlSeconds?: number;
    _retry?: boolean;
  }
}

export const API_CONFIG = {
  BASE_URL: ((import.meta as any).env.VITE_API_URL as string) || 'https://back.mediocrescan.com',
  // BASE_URL: ((import.meta as any).env.VITE_API_URL as string) || 'http://localhost:3013',
  CDN_URL: ((import.meta as any).env.VITE_CDN_URL as string) || 'https://cdn.mediocrescan.com',
  SITE_URL: ((import.meta as any).env.VITE_SITE_URL as string) || 'https://mediocrescan.com',
}

export const api = axios.create({
  baseURL: API_CONFIG.BASE_URL.endsWith('/')
    ? API_CONFIG.BASE_URL.slice(0, -1)
    : API_CONFIG.BASE_URL,
  timeout: 30000,
  headers: {
    'content-type': 'application/json',
  },
});

// Injeta token em toda requisição
api.interceptors.request.use((config) => {
  const token = parseCookies()['token'];
  if (token) config.headers.authorization = `Bearer ${token}`;
  return config;
});

// Refresh automático em 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = parseCookies()['refresh_token'];
      if (refreshToken) {
        const { data } = await api.post('auth/refresh', { refresh_token: refreshToken });
        const newToken = data?.access_token ?? data?.token;
        if (newToken) {
          setCookie(null, 'token', newToken, {
            maxAge: 60 * 60 * 24 * 7,
            path: '/',
            secure: (import.meta as any).env.PROD,
            sameSite: 'strict',
          });
          original.headers.authorization = `Bearer ${newToken}`;
          return api(original);
        }
      }
    }
    return Promise.reject(error);
  }
);

export const saveAuthData = (_token: string, userData: any) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('userData', JSON.stringify(userData));
  }
};

/** Corpo JSON quando o cliente usa axios (`response.data`). */
export function unwrapApiResponse<T = unknown>(response: unknown): T {
  if (response && typeof response === 'object' && 'data' in (response as object)) {
    return (response as { data: T }).data;
  }
  return response as T;
}

/** Mensagem amigável de erro da API (corpo `message`) em falhas axios; evita o texto genérico do Axios. */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    const raw = data?.message;
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (Array.isArray(raw)) {
      const joined = raw.filter((x): x is string => typeof x === 'string').join('; ');
      if (joined.trim()) return joined.trim();
    }
  }
  if (error instanceof Error && error.message) {
    if (!/^Request failed with status code \d+$/.test(error.message)) return error.message;
  }
  return fallback;
}
